/**
 * chat.js — Core Chat Feature
 *
 * STAGE: Base — always loaded.
 *
 * RESPONSIBILITIES:
 *   - Listen for user input (prompt chip click, input submit)
 *   - Push user message to MessageList
 *   - Emit ws-chat-request for downstream handlers
 *     (BE call, mock reply, etc — wired in later step)
 *
 * EVENTS CONSUMED:
 *   ws-prompt-select { label, value }
 *   ws-chat-input-submit { text }
 *
 * EVENTS EMITTED:
 *   ws-chat-request { text, source }
 *     source: 'prompt' | 'input'
 *
 * BE DEPENDENCY:
 *   None here — chat.js only echoes the user message and emits
 *   ws-chat-request. The BE call lives in features/base/chat-handler.js.
 */

import { on, emit, addMessage, getState, isChatDisabled } from "../../core/state.js";
import { APP_ENV } from "../../core/env.js";
import { isDrawerOpen, closeWineDrawer, getActiveDrawerContext } from "./wine-drawer.js";

// Dev-only test shortcut: exact "customerId=<id>" (optional spaces around =).
// Handled by account.js → login-greeting + View cart / orders / Membership.
// Anything else goes through normal chat. Disabled in production.
function parseDevCustomerIdCommand(text) {
  if (APP_ENV === "production") return null;
  const m = String(text || "")
    .trim()
    .match(/^customerId\s*=\s*(\S+)$/i);
  return m ? m[1] : null;
}

export async function init() {
  on("ws-prompt-select", ({ label, value, uniqueId, quizStatus, suppressUserBubble }) => {
    // Sentinel values (e.g. __contact__, __login__, __view_cart__) are local
    // actions owned by feature modules (variant contact flow, account, cart) —
    // they are not chat queries, so don't echo a user bubble or call the BE.
    if (typeof value === "string" && /^__.*__$/.test(value)) return;
    // Quiz variant may set quiz.status on this same event before we run.
    // Prefer the chip's quizStatus, then session quiz state (Wine Engine start_quiz).
    const resolvedQuizStatus =
      quizStatus || getState().quiz?.status || "";
    if (!suppressUserBubble) {
      addMessage({
        role: "user",
        kind: "text",
        // Not a bot "first response" — that path holds scroll and can hide the
        // typing dots until feature cards arrive (Shop Our Wines).
        isFirstResponse: false,
        data: { text: value || label },
      });
    }
    emit("ws-chat-request", {
      text: value || label,
      source: "prompt",
      uniqueId,
      quizStatus: resolvedQuizStatus || undefined,
    });
  });

  on("ws-chat-input-submit", ({ text, usedStt }) => {
    if (!text) return;
    if(isChatDisabled()) return;
    const quiz = getState().quiz || {};
    if (quiz.status === "completed") return;

    const devCustomerId = parseDevCustomerIdCommand(text);
    if (devCustomerId) {
      emit("ws-dev-customer-login", { customerId: devCustomerId });
      return;
    }

    let drawerContext = null;
    if (isDrawerOpen()) {
      drawerContext = getActiveDrawerContext();
      closeWineDrawer();
    }

    const userMsg = {
      role: "user",
      kind: "text",
      isFirstResponse: false,
      data: { text },
    };
    if (drawerContext) {
      const tabLabels = {
        "tasting-notes": "Tasting Notes",
        "food-pairings": "Food Pairings",
        recipe: "Recipe",
      };
      userMsg.meta = {
        drawerWine: drawerContext.wineTitle || "",
        drawerTab: tabLabels[drawerContext.tab] || "",
        drawerOpenPayload: {
          uniqueId: drawerContext.uniqueId || "",
          wineKey: drawerContext.wineKey || "",
          variantId: drawerContext.variantId || "",
          foodPairingsTaskId: drawerContext.foodPairingsTaskId || "",
          initialTab: drawerContext.tab || "tasting-notes",
          recipeTitle: drawerContext.recipeTitle || "",
        },
      };
    }
    addMessage(userMsg);
    const payload = { text, source: "input", usedStt: !!usedStt };
    if (quiz.status === "in_progress") {
      payload.quizStatus = "in_progress";
    }
    if (drawerContext) {
      payload.drawerContext = drawerContext;
    }
    emit("ws-chat-request", payload);
  });

  console.info("[RedConcierge] Chat feature initialized");
}
